import {
  GET_LIBRARY_WORKOUTS,
  GET_LIBRARY_WORKOUTS_SUCCESS,
  GET_LIBRARY_WORKOUTS_ERROR,
  CREATE_WORKOUT,
  CREATE_WORKOUT_SUCCESS,
  CREATE_WORKOUT_ERROR,
  UPDATE_LIBRARY_WORKOUT,
  UPDATE_LIBRARY_WORKOUT_SUCCESS,
  UPDATE_LIBRARY_WORKOUT_ERROR,
  DELETE_LIBRARY_WORKOUT,
  DELETE_LIBRARY_WORKOUT_SUCCESS,
  GET_USER_WORKOUTS,
  GET_USER_WORKOUTS_SUCCESS,
  GET_USER_WORKOUTS_ERROR,
  SET_IS_WORKOUT_EDIT,
  CLEAR_EDIT_WORKOUT,
  CREATE_USER_GENERATED_WORKOUT,
  CREATE_USER_GENERATED_WORKOUT_SUCCESS,
  CREATE_USER_GENERATED_WORKOUT_ERROR,
  SHARE_MY_WORKOUT,
  SHARE_MY_WORKOUT_SUCCESS,
  SHARE_MY_WORKOUT_ERROR,
  RESET_WORKOUT_LOCAL_STATE,
  SET_AI_PREFILL_WORKOUT,
  CLEAR_AI_PREFILL_WORKOUT,
  CLEAR_LAST_SAVED_LIBRARY_WORKOUT,
} from './constants';

const initialState = {
  workouts: [],
  pagination: {
    page: 1,
    limit: 50,
    total: 0,
    pages: 0,
    hasNext: false,
    hasPrev: false,
  },
  stats: {
    total: 0,
    active: 0,
    averageExercises: 0,
    recent: 0,
  },
  getWorkoutsLoading: false,
  getWorkoutsErr: null,
  createWorkoutLoading: false,
  createWorkoutErr: null,
  createWorkoutSuccess: false,
  isExerciseEdit: false,
  userWorkouts: [],
  // Whose `userWorkouts` is currently in the slice. Used to wipe stale
  // data when navigating between clients so the list doesn't briefly
  // show the previous client's workouts during the in-flight fetch.
  userWorkoutsClientId: null,
  userWorkoutsPagination: null,
  deleteWorkoutLoading: { workoutId: '', loading: false },
  isWorkoutEdit: false,
  isWorkoutDraft: true,
  workoutToShare: null,
  resetLocalSignal: null,
  // Staging slot for an AI-generated workout draft. Set on success
  // from AiGenerateWorkoutModal; consumed + cleared by
  // createWorkoutModal when the editor opens.
  aiPrefillWorkout: null,
  // The library doc the most recent createWorkout / updateLibraryWorkout
  // success returned. Stamped here regardless of whether the doc was
  // newly created, promoted from a draft, or updated in place — the
  // shared signal "this save produced a finalized library doc" is what
  // the save-time tag-suggestion modal listens for. Cleared by
  // createWorkoutModal once consumed so a future modal open doesn't
  // re-fire the suggester on stale data.
  lastSavedLibraryWorkout: null,
};

function workouts(state = initialState, action) {
  const { type, payload } = action;
  switch (type) {
    case GET_LIBRARY_WORKOUTS:
      return {
        ...state,
        getWorkoutsErr: false,
        getWorkoutsLoading: true,
      };
    case GET_LIBRARY_WORKOUTS_SUCCESS:
      // Handle both old format (array) and new format (object with workouts and pagination)
      if (Array.isArray(payload)) {
        // Old format: array of workouts - calculate stats from payload
        const total = payload.length;
        const active = payload.filter((w) => w.isActive !== false).length;
        const averageExercises =
          total > 0
            ? payload.reduce((acc, w) => acc + (w.exercises?.length || 0), 0) /
              total
            : 0;
        const recent = payload.filter((w) => {
          const daysSince =
            (Date.now() - new Date(w.createdAt)) / (1000 * 60 * 60 * 24);
          return daysSince <= 7;
        }).length;

        return {
          ...state,
          workouts: payload,
          pagination: {
            page: 1,
            limit: payload.length,
            total: payload.length,
            pages: 1,
            hasNext: false,
            hasPrev: false,
          },
          stats: {
            total,
            active,
            averageExercises,
            recent,
          },
          getWorkoutsLoading: false,
        };
      } else {
        // New format: object with workouts, pagination, and stats
        return {
          ...state,
          workouts: payload.workouts || [],
          pagination: payload.pagination || {
            page: 1,
            limit: 50,
            total: 0,
            pages: 0,
            hasNext: false,
            hasPrev: false,
          },
          stats: payload.stats || {
            total: payload.pagination?.total || 0,
            active: 0,
            averageExercises: 0,
            recent: 0,
          },
          getWorkoutsLoading: false,
        };
      }
    case GET_LIBRARY_WORKOUTS_ERROR:
      return {
        ...state,
        getWorkoutsLoading: false,
        getWorkoutsErr: action.err,
      };
    case CREATE_WORKOUT:
      return {
        ...state,
        createWorkoutLoading: true,
        createWorkoutErr: null,
        createWorkoutSuccess: false,
      };
    case CREATE_WORKOUT_SUCCESS: {
      const resolvedDraftStatus =
        payload?.workout?.draft ??
        payload?.workoutLibrary?.draft ??
        state.isWorkoutDraft;
      // Logging a client draft replaces it: the API deletes the draft and
      // names it here, so its card leaves the library list.
      const removedDraftId = payload?.removedDraftId
        ? String(payload.removedDraftId)
        : null;
      const hadRemovedDraft =
        !!removedDraftId &&
        state.workouts.some((w) => String(w._id) === removedDraftId);
      const remainingWorkouts = hadRemovedDraft
        ? state.workouts.filter((w) => String(w._id) !== removedDraftId)
        : state.workouts;
      return {
        ...state,
        workouts: action.payload.hasOwnProperty('workoutLibrary')
          ? [
              action.payload.workoutLibrary,
              ...remainingWorkouts.filter(
                (workout) => workout._id !== action.payload.workoutIdToRemove
              ),
            ]
          : remainingWorkouts,
        // Surface the just-saved library doc for the tag-suggestion
        // modal. Only set when the response actually carried a library
        // doc — session-only saves (Save & Assign toggle OFF) skip
        // this slot so the modal never re-fires on stale data.
        lastSavedLibraryWorkout: action.payload.hasOwnProperty(
          'workoutLibrary'
        )
          ? action.payload.workoutLibrary
          : state.lastSavedLibraryWorkout,
        userWorkouts: action.payload.hasOwnProperty('workout')
          ? [
              payload.workout,
              ...(Array.isArray(state.userWorkouts) ? state.userWorkouts : []),
            ]
          : state.userWorkouts,
        createWorkoutErr: null,
        createWorkoutSuccess: true,
        createWorkoutLoading: false,
        isWorkoutDraft: resolvedDraftStatus,
        // Update pagination total when a new workout is created.
        // Existing draft updates send `workoutIdToRemove` set to the
        // draft's own _id (whether from the draft branch's update path
        // or from the new Save & Assign promotion path) — in that case
        // we're REPLACING an existing entry, not adding one, so the
        // total should not increment.
        pagination: {
          ...state.pagination,
          total:
            state.pagination.total +
            (action.payload.hasOwnProperty('workoutLibrary') &&
            !state.workouts.some(
              (w) => w._id === action.payload.workoutIdToRemove
            )
              ? 1
              : 0) -
            (hadRemovedDraft ? 1 : 0),
        },
      };
    }
    case CREATE_WORKOUT_ERROR:
      return {
        ...state,
        createWorkoutLoading: false,
        createWorkoutErr: action.err,
        createWorkoutSuccess: false,
      };
    case UPDATE_LIBRARY_WORKOUT:
      return {
        ...state,
        createWorkoutLoading: true,
        createWorkoutErr: null,
        createWorkoutSuccess: false,
      };
    case UPDATE_LIBRARY_WORKOUT_SUCCESS: {
      // Publishing or unpublishing moves a template in or out of the
      // library's "Active" stat (it counts published templates only), so
      // keep that tile in step without a refetch.
      const before = state.workouts.find((w) => w._id === payload._id);
      const isActive = (w) => (w && w.draft !== true ? 1 : 0);
      const activeDelta = before ? isActive(payload) - isActive(before) : 0;
      return {
        ...state,
        workouts: state.workouts.map((workout) =>
          workout._id === payload._id ? payload : workout
        ),
        stats:
          activeDelta && state.stats
            ? {
                ...state.stats,
                active: Math.max(0, (state.stats.active || 0) + activeDelta),
              }
            : state.stats,
        createWorkoutErr: null,
        createWorkoutSuccess: true,
        createWorkoutLoading: false,
        // Surface the just-updated library doc so the save-time tag
        // suggestion modal can pick up edits to existing templates the
        // same way it picks up new creations.
        lastSavedLibraryWorkout: payload,
      };
    }
    case UPDATE_LIBRARY_WORKOUT_ERROR:
      return {
        ...state,
        createWorkoutLoading: false,
        createWorkoutErr: action.err,
        createWorkoutSuccess: false,
      };
    case DELETE_LIBRARY_WORKOUT:
      return {
        ...state,
        deleteWorkoutLoading: { id: action.id, loading: true },
      };
    case DELETE_LIBRARY_WORKOUT_SUCCESS:
      return {
        ...state,
        workouts: state.workouts.filter((workout) => workout._id !== payload),
        deleteWorkoutLoading: { id: action.payload, loading: false },
        // Update pagination total when a workout is deleted
        pagination: {
          ...state.pagination,
          total: Math.max(0, (state.pagination?.total || 0) - 1),
          pages:
            state.pagination?.total > 1
              ? Math.ceil(
                  Math.max(0, (state.pagination?.total || 0) - 1) /
                    (state.pagination?.limit || 50)
                )
              : 1,
        },
      };
    case GET_USER_WORKOUTS: {
      // Wipe the slice when the fetch is for a different client than
      // what's currently cached. Same-client refetches (pagination,
      // post-mutation reload) keep their data so the UI stays stable
      // during the network round-trip.
      const isDifferentClient =
        action.clientId &&
        state.userWorkoutsClientId &&
        state.userWorkoutsClientId !== action.clientId;
      return {
        ...state,
        getWorkoutsErr: false,
        getWorkoutsLoading: true,
        userWorkouts: isDifferentClient ? [] : state.userWorkouts,
        userWorkoutsPagination: isDifferentClient
          ? null
          : state.userWorkoutsPagination,
        userWorkoutsClientId: action.clientId || state.userWorkoutsClientId,
      };
    }
    case GET_USER_WORKOUTS_SUCCESS:
      // Paginated response: { workouts, pagination }
      if (payload && payload.workouts && Array.isArray(payload.workouts)) {
        const isFirstPage = payload.pagination && payload.pagination.page === 1;
        return {
          ...state,
          userWorkouts: isFirstPage
            ? payload.workouts
            : [...(Array.isArray(state.userWorkouts) ? state.userWorkouts : []), ...payload.workouts],
          userWorkoutsPagination: payload.pagination || null,
          getWorkoutsLoading: false,
        };
      }
      // Stats response: { currentWeek, averageMonth }
      if (payload && payload.currentWeek) {
        return {
          ...state,
          userWorkouts: payload,
          userWorkoutsPagination: null,
          getWorkoutsLoading: false,
        };
      }
      // Legacy array response
      return {
        ...state,
        userWorkouts: Array.isArray(payload) ? payload : [],
        userWorkoutsPagination: null,
        getWorkoutsLoading: false,
      };
    case GET_USER_WORKOUTS_ERROR:
      return {
        ...state,
        getWorkoutsLoading: false,
        getWorkoutsErr: action.err,
      };
    case SET_IS_WORKOUT_EDIT:
      return {
        ...state,
        isWorkoutEdit: action.value,
      };
    case CLEAR_EDIT_WORKOUT:
      return {
        ...state,
        isWorkoutEdit: false,
      };
    case CREATE_USER_GENERATED_WORKOUT:
      return {
        ...state,
        createWorkoutLoading: true,
        createWorkoutErr: null,
        createWorkoutSuccess: false,
      };
    case CREATE_USER_GENERATED_WORKOUT_SUCCESS:
      return {
        ...state,
        userWorkouts: [
          payload,
          ...(Array.isArray(state.userWorkouts) ? state.userWorkouts : []),
        ],
        createWorkoutErr: null,
        createWorkoutSuccess: true,
        createWorkoutLoading: false,
        isWorkoutDraft: false,
      };
    case CREATE_USER_GENERATED_WORKOUT_ERROR:
      return {
        ...state,
        createWorkoutLoading: false,
        createWorkoutErr: action.err,
        createWorkoutSuccess: false,
      };
    case SHARE_MY_WORKOUT:
      return {
        ...state,
        userWorkouts: state.userWorkouts,
        workoutToShare: null,
        getWorkoutsLoading: true,
      };
    case SHARE_MY_WORKOUT_SUCCESS:
      return {
        ...state,
        workoutToShare: payload,
        getWorkoutsLoading: false,
      };
    case SHARE_MY_WORKOUT_ERROR:
      return {
        ...state,
        workoutToShare: null,
        getWorkoutsLoading: false,
        getWorkoutsErr: action.err,
      };
    case RESET_WORKOUT_LOCAL_STATE:
      return {
        ...state,
        resetLocalSignal: Date.now(),
      };
    case SET_AI_PREFILL_WORKOUT:
      return {
        ...state,
        aiPrefillWorkout: action.workout || null,
      };
    case CLEAR_AI_PREFILL_WORKOUT:
      return {
        ...state,
        aiPrefillWorkout: null,
      };
    case CLEAR_LAST_SAVED_LIBRARY_WORKOUT:
      return {
        ...state,
        lastSavedLibraryWorkout: null,
      };
    default:
      return state;
  }
}

export default workouts;
